/**
 * Interactive Educational Data Table with cell animations and row highlights.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface TableProps {
  headers: string[];
  rows: string[][];
  cellWidth?: number;
  cellHeight?: number;
  headerBg?: string;
  rowBg?: string;
  altRowBg?: string;
  textColor?: string;
  highlightedRow?: number;
}

export class TableObject extends SVGObject {
  public props: TableProps;

  constructor(config: SVGObjectConfig & { props?: TableProps }) {
    super({ ...config, type: 'table' });
    this.props = {
      headers: ['Col 1', 'Col 2', 'Col 3'],
      rows: [
        ['A1', 'B1', 'C1'],
        ['A2', 'B2', 'C2']
      ],
      cellWidth: 140,
      cellHeight: 44,
      headerBg: '#1e293b',
      rowBg: '#0f172a',
      altRowBg: '#1e1e2e',
      textColor: '#f8fafc',
      highlightedRow: -1,
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const headers = this.props.headers || [];
    const rows = this.props.rows || [];
    const cW = this.props.cellWidth || 140;
    const cH = this.props.cellHeight || 44;
    const totalW = headers.length * cW;

    // 1. Headers
    headers.forEach((h, col) => {
      const rect = Renderer.createElement('rect');
      Renderer.setAttributes(rect, {
        x: col * cW,
        y: 0,
        width: cW,
        height: cH,
        fill: this.props.headerBg || '#1e293b',
        stroke: '#334155',
        'stroke-width': 1
      });
      this.container.appendChild(rect);

      const text = Renderer.createElement('text');
      Renderer.setAttributes(text, {
        x: col * cW + cW / 2,
        y: cH / 2,
        'font-size': 14,
        'font-family': 'Inter, sans-serif',
        'font-weight': '700',
        fill: '#38bdf8',
        'text-anchor': 'middle',
        'dominant-baseline': 'central'
      });
      text.textContent = h;
      this.container.appendChild(text);
    });

    // 2. Data Rows
    rows.forEach((row, rIdx) => {
      const y = (rIdx + 1) * cH;
      const isHigh = this.props.highlightedRow === rIdx;
      const bg = isHigh ? '#0284c7' : rIdx % 2 === 0 ? this.props.rowBg : this.props.altRowBg;

      row.forEach((val, cIdx) => {
        const rect = Renderer.createElement('rect');
        Renderer.setAttributes(rect, {
          x: cIdx * cW,
          y,
          width: cW,
          height: cH,
          fill: bg,
          stroke: '#334155',
          'stroke-width': 1
        });
        this.container.appendChild(rect);

        const text = Renderer.createElement('text');
        Renderer.setAttributes(text, {
          x: cIdx * cW + cW / 2,
          y: y + cH / 2,
          'font-size': 13,
          'font-family': 'Inter, sans-serif',
          'font-weight': isHigh ? '700' : '400',
          fill: this.props.textColor || '#f8fafc',
          'text-anchor': 'middle',
          'dominant-baseline': 'central'
        });
        text.textContent = val;
        this.container.appendChild(text);
      });
    });
  }

  public setHighlightedRow(rowIdx: number): void {
    this.props.highlightedRow = rowIdx;
    this.render();
  }
}
